import * as React from 'react';
import { connect } from 'react-redux';
import V3CollapsibleSection from '../../components/V3CollapsibleSection';
import V3SpecsTable from '../../components/V3SpecsTable';
import V3SpecRow from '../../components/V3SpecRow';
import V3UnitToggle from '../../components/V3UnitToggle';
import V3Button from '../../components/V3Button';
import { SET_PROJECT_OPTION, RESET_PANELS_STATE } from 'actions';
import { SET_CUSTOM_VALUE, FETCH_MFG_MODELS_REQUEST, SET_MFG, SET_MODEL } from '__common/components/moduleSelector/moduleSelectorActions';
import { MARK_LITE_DIRTY } from 'app/appActions';
import { deleteAllPanels } from '__editor/panelsEditor/components/panels/utils/panelsManagment';
import { SAVE_PANELS_TO_DRAWING_MANAGER } from '__editor/googleMapsRoofsSelector/components/drawingManager/drawingManagerActions';
import config from 'config';
import { state as getReduxState } from '__common/store';
import { getDefaultRoofPitchValue } from '__editor/panelsEditor/components/tiltedRoof/tiltedRoof';
import {
  products,
  isNxtHorizon,
  isNxtTilt,
  isRM5,
  isRMDT,
  isRmGridflex10D,
  isRmGridflex5Rwdi,
  isRM10,
  isRM10Evolution,
  isRMGridFlex,
} from '__common/constants/products';
import { validateWidth, validateLength } from '__common/components/moduleSelector/components/customModuleValidation';
import { checkThickness } from '__editor/components/roofsSelector/components/roofsSelectorSaveLoadProject/utils/validateProject';
import { InputUnit, isMetricUnit } from 'engineering/components/engineeringProjectDocuments/utils/unitTypes';

type OwnProps = {
  unitMode: string;
  onUnitChange: (unit: string) => void;
  onPanelsCleared?: () => void;
};

type ApiMfg = { id: number; name: string };
type ApiModel = { id: number; name: string; width?: string; height?: string; thickness?: string; weight?: string; watts?: number; max_output_watts?: number };

type ModuleSpecs = {
  output?: number;
  widthIn?: number;
  lengthIn?: number;
  thicknessIn?: number;
  weightLbs?: number;
  areaFt?: number;
};

type Props = OwnProps & {
  dispatch: Function;
  manufacturerName: string;
  modelName: string;
  specs: ModuleSpecs;
  panelCount: number;
  productId: number | undefined;
  inputUnit: number | undefined;
  productSlug: string;
  mfgs: ApiMfg[];
  models: ApiModel[];
  mfgsLoading: boolean;
  modelsLoading: boolean;
};

// ModuleSpecs key → moduleSelector.modelData key. Autosave's
// parseModelData() (saveProject.ts:570-573) reads modelData fields
// directly into the PUT payload, so dispatching SET_PROJECT_OPTION on
// projectEnvConfig.module_specs alone gets clobbered at save time.
const MODEL_DATA_FIELD: { [k in keyof ModuleSpecs]?: string } = {
  output: 'watts',
  widthIn: 'width',
  lengthIn: 'height',
  thicknessIn: 'thickness',
  weightLbs: 'weight',
};

// Convert an imperial value (in/lbs) to the unit modelData stores in,
// which is determined by the project's input_unit (not the UI toggle).
// convertModelData (moduleSelector.ts:73) shows METRIC modelData uses cm
// for length-y fields and kg for weight.
function imperialToModelDataUnit(field: keyof ModuleSpecs, imperialValue: number, projectInputUnit: number | undefined): number {
  if (!isMetricUnit(projectInputUnit as number)) return imperialValue;
  if (field === 'widthIn' || field === 'lengthIn' || field === 'thicknessIn') {
    return imperialValue * 2.54; // in → cm
  }
  if (field === 'weightLbs') {
    return imperialValue * 0.453592; // lbs → kg
  }
  return imperialValue;
}

type State = {
  // Raw mirror of what the user typed in each spec input. Indexed by the
  // ModuleSpecs field key. Without this, the controlled inputs reformat
  // every keystroke and the user gets stuck at 0.30 (or whatever was
  // previously valid) the moment they type something out-of-range.
  // Cleared per-field once the autosave dispatch fires for that field.
  specRaw: { [field: string]: string };
  // Pending destructive dimension change waiting on user confirm/cancel.
  // Width / length / thickness edits with placed panels invalidate the
  // existing panel layout, so we hold the new value here while the modal
  // asks the user; on confirm we dispatch RESET_PANELS_STATE then apply.
  pendingDimChange: { field: keyof ModuleSpecs; value: number; toImperialMultiplier: number } | null;
  pendingModelChange: ApiModel | null;
  pendingMfgChange: ApiMfg | null;
  mfgFilter: string;
  mfgOpen: boolean;
  modelFilter: string;
  modelOpen: boolean;
};

// Product-specific thickness range message, mirroring legacy
// customModuleSelector.tsx:503's errMsg() switch.
const thicknessErrorMessage = (productId: number | undefined): string => {
  if (productId === undefined) return 'Minimum Module thickness is 30mm / 1.18in.';
  if (isRM5(productId) || isRMDT(productId) || isRM10Evolution(productId)) {
    return 'Module thickness should be >= 30mm / 1.18in and <= 50mm / 1.96in.';
  }
  if (isRmGridflex10D(productId)) {
    return 'Module thickness should be >= 30mm / 1.18in and <= 45mm / 1.77in.';
  }
  if (isRmGridflex5Rwdi(productId)) {
    return 'Module thickness should be >= 30mm / 1.18in and <= 45mm / 1.77in.';
  }
  if (isRM10(productId) || isRMGridFlex(productId)) {
    return 'Thickness should be greater than 0.';
  }
  if (isNxtHorizon(productId) || isNxtTilt(productId)) {
    return 'Module thickness should be >= 30mm / 1.18in and <= 40mm / 1.57in.';
  }
  return 'Minimum Module thickness is 30mm / 1.18in.';
};

const ModuleIcon = (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <rect x="1" y="1" width="5" height="4" rx="1" fill="currentColor" opacity=".7"/>
    <rect x="8" y="1" width="5" height="4" rx="1" fill="currentColor"/>
    <rect x="1" y="7" width="5" height="4" rx="1" fill="currentColor"/>
    <rect x="8" y="7" width="5" height="4" rx="1" fill="currentColor" opacity=".5"/>
  </svg>
);

// Imperial → metric display conversions. Storage stays imperial.
const IN_TO_MM = 25.4;
const LB_TO_KG = 0.453592;
const FT2_TO_M2 = 0.092903;

const fmt = (value: number | undefined, decimals: number): string => {
  if (value === undefined || value === null || isNaN(value as number)) return '—';
  return (value as number).toFixed(decimals);
};

const DIMENSION_FIELDS: Array<keyof ModuleSpecs> = ['widthIn', 'lengthIn', 'thicknessIn'];

const CUSTOM_MODEL_ID = 1000001;

class ModuleSection extends React.Component<Props, State> {
  state: State = { specRaw: {}, pendingDimChange: null, pendingModelChange: null, pendingMfgChange: null,
    mfgFilter: '', mfgOpen: false, modelFilter: '', modelOpen: false };

  private mfgRef = React.createRef<HTMLDivElement>();
  private modelRef = React.createRef<HTMLDivElement>();

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside);
    this.setState({ mfgFilter: this.props.manufacturerName, modelFilter: this.props.modelName });
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.manufacturerName !== this.props.manufacturerName && !this.state.mfgOpen) {
      this.setState({ mfgFilter: this.props.manufacturerName });
    }
    if (prevProps.modelName !== this.props.modelName && !this.state.modelOpen) {
      this.setState({ modelFilter: this.props.modelName });
    }
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
  }

  handleClickOutside = (e: MouseEvent) => {
    if (this.mfgRef.current && !this.mfgRef.current.contains(e.target as Node)) {
      this.setState({ mfgOpen: false });
    }
    if (this.modelRef.current && !this.modelRef.current.contains(e.target as Node)) {
      this.setState({ modelOpen: false });
    }
  };

  selectMfg = (mfg: ApiMfg) => {
    this.setState({ mfgFilter: mfg.name, mfgOpen: false, modelFilter: '', modelOpen: false });
    if (mfg.name !== this.props.manufacturerName && this.hasPanelsAnywhere()) {
      this.setState({ pendingMfgChange: mfg });
      return;
    }
    this.applyMfgChange(mfg);
  };

  applyMfgChange = (mfg: ApiMfg) => {
    const version = config.currentProjectVersion;
    this.props.dispatch(SET_MFG(mfg.id));
    this.props.dispatch(FETCH_MFG_MODELS_REQUEST(this.props.productSlug, mfg.id, null, undefined, version));
    this.props.dispatch(SET_PROJECT_OPTION('manufacturer_name', mfg.name));
    this.props.dispatch(SET_PROJECT_OPTION('module_manufacturer', mfg.name));
    this.props.dispatch(SET_PROJECT_OPTION('model_name', ''));
    this.props.dispatch(SET_PROJECT_OPTION('module_model', ''));
  };

  confirmMfgChange = () => {
    const mfg = this.state.pendingMfgChange;
    if (!mfg) return;
    this.setState({ pendingMfgChange: null });
    this.clearAllRoofPanels();
    this.applyMfgChange(mfg);
    this.props.dispatch(MARK_LITE_DIRTY());
    if (this.props.onPanelsCleared) this.props.onPanelsCleared();
  };

  cancelMfgChange = () => {
    this.setState({ pendingMfgChange: null, mfgFilter: this.props.manufacturerName });
  };

  hasSameDims = (model: ApiModel): boolean => {
    const { specs } = this.props;
    const mw = model.width ? parseFloat(model.width) : 0;
    const mh = model.height ? parseFloat(model.height) : 0;
    const cw = specs.widthIn || 0;
    const ch = specs.lengthIn || 0;
    return Math.abs(mw - cw) < 0.01 && Math.abs(mh - ch) < 0.01;
  };

  hasPanelsAnywhere = (): boolean => {
    if (this.props.panelCount > 0) return true;
    const s = getReduxState() as any;
    const roofs = s && s.drawingManager && s.drawingManager.roofs;
    if (!roofs) return false;
    return Object.keys(roofs).some((id) => roofs[id] && roofs[id].panels && roofs[id].panels.length > 0);
  };

  clearAllRoofPanels = () => {
    deleteAllPanels();
    const s = getReduxState() as any;
    const roofs = s && s.drawingManager && s.drawingManager.roofs;
    if (roofs) {
      Object.keys(roofs).forEach((id) => {
        const r = roofs[id];
        if (!r) return;
        this.props.dispatch(SAVE_PANELS_TO_DRAWING_MANAGER(
          r.id ?? Number(id), [], r.bgRotation ?? 0, r.bgOffset ?? { x: 0, y: 0 },
          r.bgScale ?? { x: 1, y: 1 },
          r.roofPitch ?? getDefaultRoofPitchValue(Number(this.props.productId), 1, 1, undefined, true),
          r.structureTilt ?? '',
          r.metersPerPixel ?? 0, r.zoom ?? 19, r.rowSpacing ?? 0, r.tableRowSpacing ?? 0,
          r.roofEdgesPixiCords ?? [], r.exposureRecaluclationHash ?? '',
          r.blank_map_building_length ?? 0, r.blank_map_building_width ?? 0,
          r.panelsRotationDegrees ?? 0, r.lowEdgeToRoof ?? 50, r.panelBayMapping ?? new Map(),
        ));
      });
    }
  };

  selectModel = (model: ApiModel) => {
    this.setState({ modelFilter: model.name, modelOpen: false });
    if (!this.hasSameDims(model) && model.id !== CUSTOM_MODEL_ID && this.hasPanelsAnywhere()) {
      this.setState({ pendingModelChange: model });
      return;
    }
    this.applyModelChange(model);
  };

  applyModelChange = (model: ApiModel) => {
    const width = model.width ? parseFloat(model.width) : 0;
    const length = model.height ? parseFloat(model.height) : 0;
    const newSpecs: ModuleSpecs = {
      output: model.watts || model.max_output_watts || 0,
      widthIn: width, lengthIn: length,
      thicknessIn: model.thickness ? parseFloat(model.thickness) : 0,
      weightLbs: model.weight ? parseFloat(model.weight) : 0,
      areaFt: width && length ? (width * length) / 144 : 0,
    };
    this.props.dispatch(SET_PROJECT_OPTION('model_name', model.name));
    this.props.dispatch(SET_PROJECT_OPTION('module_model', model.name));
    this.props.dispatch(SET_PROJECT_OPTION('model', model.id));
    this.props.dispatch(SET_PROJECT_OPTION('module_specs', newSpecs));
    this.props.dispatch(SET_PROJECT_OPTION('module_width', newSpecs.widthIn));
    this.props.dispatch(SET_PROJECT_OPTION('module_length', newSpecs.lengthIn));
    this.props.dispatch(SET_PROJECT_OPTION('module_thickness', newSpecs.thicknessIn));
    this.props.dispatch(SET_PROJECT_OPTION('module_weight', newSpecs.weightLbs));
    this.props.dispatch(SET_PROJECT_OPTION('module_watts', newSpecs.output));
    this.props.dispatch(SET_MODEL(model.id, undefined, products[this.props.productSlug]));
    this.props.dispatch(MARK_LITE_DIRTY());
  };

  confirmModelChange = () => {
    const model = this.state.pendingModelChange;
    if (!model) return;
    this.setState({ pendingModelChange: null });
    this.clearAllRoofPanels();
    this.applyModelChange(model);
    this.props.dispatch(MARK_LITE_DIRTY());
    if (this.props.onPanelsCleared) this.props.onPanelsCleared();
  };

  cancelModelChange = () => {
    this.setState({ pendingModelChange: null, modelFilter: this.props.modelName });
  };

  getFilteredMfgs = (): ApiMfg[] => {
    const { mfgs } = this.props;
    const q = (this.state.mfgFilter || '').toLowerCase();
    if (!q) return mfgs;
    if (mfgs.some((m) => m.name.toLowerCase() === q)) return mfgs;
    return mfgs.filter((m) => m.name.toLowerCase().includes(q));
  };

  getFilteredModels = (): ApiModel[] => {
    const { models } = this.props;
    const q = (this.state.modelFilter || '').toLowerCase();
    if (!q) return models;
    if (models.some((m) => m.name.toLowerCase() === q)) return models;
    return models.filter((m) => m.name.toLowerCase().includes(q));
  };

  // Validate the current imperial spec values per product. Returns errors
  // keyed by spec field. Mirrors the Step 3 validation in NewProjectModal:
  // shared validators from customModuleValidation + checkThickness.
  computeErrors = (specs: ModuleSpecs): { width?: string; length?: string; thickness?: string } => {
    const { productId } = this.props;
    const errs: { width?: string; length?: string; thickness?: string } = {};
    if (productId === undefined || productId === null) return errs;
    if (specs.widthIn !== undefined && specs.widthIn !== null) {
      const widthErr = validateWidth(specs.widthIn, productId, false, InputUnit.CUSTOMARY);
      if (widthErr) errs.width = widthErr;
    }
    if (specs.lengthIn !== undefined && specs.lengthIn !== null) {
      const lengthErr = validateLength(specs.lengthIn, productId, InputUnit.CUSTOMARY);
      if (lengthErr) errs.length = lengthErr;
    }
    if (specs.thicknessIn !== undefined && specs.thicknessIn !== null) {
      if (!checkThickness(specs.thicknessIn, productId)) {
        errs.thickness = thicknessErrorMessage(productId);
      }
    }
    return errs;
  };

  // Maps a spec field key to its corresponding error key (or null for
  // fields that aren't validated, like output/weight/area).
  private errorKeyForField = (field: keyof ModuleSpecs): 'width' | 'length' | 'thickness' | null => {
    if (field === 'widthIn') return 'width';
    if (field === 'lengthIn') return 'length';
    if (field === 'thicknessIn') return 'thickness';
    return null;
  };

  // Inner work: validate, dispatch SET_PROJECT_OPTION + mirror to
  // moduleSelector.modelData via SET_CUSTOM_VALUE so saveProject's
  // parseModelData() picks up the change at autosave time.
  applySpecChange = (field: keyof ModuleSpecs, imperialValue: number) => {
    const nextSpecs: ModuleSpecs = { ...this.props.specs, [field]: imperialValue };
    if (field === 'widthIn' || field === 'lengthIn') {
      const w = nextSpecs.widthIn || 0;
      const l = nextSpecs.lengthIn || 0;
      nextSpecs.areaFt = (w * l) / 144;
    }
    // Only dispatch (= persist via autosave PUT) when the new value passes
    // validation for the relevant field. Out-of-range values stay local in
    // specRaw + show inline error; the previous valid value remains in
    // projectEnvConfig.module_specs so autosave doesn't carry the bad one.
    const errKey = this.errorKeyForField(field);
    if (errKey) {
      const errs = this.computeErrors(nextSpecs);
      if (errs[errKey]) return;
    }
    this.props.dispatch(SET_PROJECT_OPTION('module_specs', nextSpecs));

    // Mirror the new value into moduleSelector.modelData so saveProject's
    // parseModelData() picks it up at autosave time. SET_CUSTOM_VALUE also
    // flips selectedMfgId to CUSTOM_MANUFACTURING_ID, matching the legacy
    // behavior where editing any spec converts the module to "custom".
    const modelDataKey = MODEL_DATA_FIELD[field];
    if (modelDataKey) {
      const modelDataValue = imperialToModelDataUnit(field, imperialValue, this.props.inputUnit);
      this.props.dispatch(SET_CUSTOM_VALUE(modelDataKey, modelDataValue));
    }
  };

  // Non-dimension fields: update on every keystroke
  updateSpec = (field: keyof ModuleSpecs, toImperialMultiplier: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    this.setState((prev) => ({ ...prev, specRaw: { ...prev.specRaw, [field]: raw } }));
    if (raw === '') return;
    const parsed = parseFloat(raw);
    if (isNaN(parsed)) return;
    this.applySpecChange(field, parsed * toImperialMultiplier);
  };

  // Dimension fields: type freely, commit on blur or Enter
  onDimInput = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    this.setState((prev) => ({ ...prev, specRaw: { ...prev.specRaw, [field]: val } }));
  };

  commitDim = (field: keyof ModuleSpecs, toImperialMultiplier: number) => () => {
    const raw = this.state.specRaw[field];
    this.setState((prev) => {
      const sr = { ...prev.specRaw };
      delete sr[field];
      return { ...prev, specRaw: sr };
    });
    if (raw === undefined || raw === '') return;
    const parsed = parseFloat(raw);
    if (isNaN(parsed)) return;
    const imperialValue = parsed * toImperialMultiplier;
    const current = (this.props.specs[field] as number) || 0;
    if (Math.abs(imperialValue - current) < 0.001) return;
    if (this.hasPanelsAnywhere()) {
      this.setState((prev) => ({ ...prev, pendingDimChange: { field, value: imperialValue, toImperialMultiplier } }));
      return;
    }
    this.applySpecChange(field, imperialValue);
  };

  onDimKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
  };

  confirmDimChange = () => {
    const pending = this.state.pendingDimChange;
    if (!pending) return;
    this.setState((prev) => ({ ...prev, pendingDimChange: null }));
    this.clearAllRoofPanels();
    this.applySpecChange(pending.field, pending.value);
    this.props.dispatch(MARK_LITE_DIRTY());
    if (this.props.onPanelsCleared) this.props.onPanelsCleared();
  };

  cancelDimChange = () => {
    this.setState((prev) => ({ ...prev, pendingDimChange: null }));
  };

  render() {
    const { unitMode, onUnitChange, manufacturerName, modelName, specs } = this.props;
    const isMetric = unitMode === 'Metric';
    const lengthUnit = isMetric ? 'mm' : 'in';
    const weightUnit = isMetric ? 'kg' : 'lbs';
    const areaUnit = isMetric ? 'm\u00B2' : 'ft\u00B2';

    const widthDisp = specs.widthIn !== undefined ? (isMetric ? specs.widthIn * IN_TO_MM : specs.widthIn) : undefined;
    const lengthDisp = specs.lengthIn !== undefined ? (isMetric ? specs.lengthIn * IN_TO_MM : specs.lengthIn) : undefined;
    const thicknessDisp = specs.thicknessIn !== undefined ? (isMetric ? specs.thicknessIn * IN_TO_MM : specs.thicknessIn) : undefined;
    const weightDisp = specs.weightLbs !== undefined ? (isMetric ? specs.weightLbs * LB_TO_KG : specs.weightLbs) : undefined;
    const areaDisp = specs.areaFt !== undefined ? (isMetric ? specs.areaFt * FT2_TO_M2 : specs.areaFt) : undefined;
    const lengthDecimals = isMetric ? 0 : 1;
    const weightDecimals = isMetric ? 1 : 1;
    const areaDecimals = isMetric ? 2 : 2;

    // Resolve each input's display value: if user is mid-edit (raw mirror
    // populated for that field), show the raw string verbatim. Otherwise
    // fall back to the formatted display from store-backed specs.
    const specRaw = this.state.specRaw || {};
    const widthValue = specRaw.widthIn !== undefined
      ? specRaw.widthIn
      : (widthDisp !== undefined ? fmt(widthDisp, lengthDecimals) : '');
    const lengthValue = specRaw.lengthIn !== undefined
      ? specRaw.lengthIn
      : (lengthDisp !== undefined ? fmt(lengthDisp, lengthDecimals) : '');
    const thicknessValue = specRaw.thicknessIn !== undefined
      ? specRaw.thicknessIn
      : (thicknessDisp !== undefined ? fmt(thicknessDisp, isMetric ? 1 : 2) : '');
    const weightValue = specRaw.weightLbs !== undefined
      ? specRaw.weightLbs
      : (weightDisp !== undefined ? fmt(weightDisp, weightDecimals) : '');
    const outputValue = specRaw.output !== undefined
      ? specRaw.output
      : (specs.output !== undefined ? String(specs.output) : '');

    // Validate against the in-progress imperial value when the user is
    // mid-edit so the input goes red as they type out-of-range, not just
    // after blur. parseFloat handles partial inputs (e.g. "0.") gracefully.
    const liveSpecs: ModuleSpecs = { ...specs };
    if (specRaw.widthIn !== undefined) {
      const n = parseFloat(specRaw.widthIn);
      liveSpecs.widthIn = isNaN(n) ? specs.widthIn : (isMetric ? n / IN_TO_MM : n);
    }
    if (specRaw.lengthIn !== undefined) {
      const n = parseFloat(specRaw.lengthIn);
      liveSpecs.lengthIn = isNaN(n) ? specs.lengthIn : (isMetric ? n / IN_TO_MM : n);
    }
    if (specRaw.thicknessIn !== undefined) {
      const n = parseFloat(specRaw.thicknessIn);
      liveSpecs.thicknessIn = isNaN(n) ? specs.thicknessIn : (isMetric ? n / IN_TO_MM : n);
    }
    const errors = this.computeErrors(liveSpecs);

    const mfgLabel = manufacturerName || '—';
    const modelLabel = modelName || '—';
    const wattsLabel = specs.output !== undefined ? `${specs.output} W` : '— W';
    const dimsLabel = widthDisp !== undefined && lengthDisp !== undefined
      ? `${fmt(widthDisp, lengthDecimals)} \u00D7 ${fmt(lengthDisp, lengthDecimals)} ${lengthUnit}`
      : `— ${lengthUnit}`;
    const thicknessChip = thicknessDisp !== undefined ? `${fmt(thicknessDisp, isMetric ? 1 : 2)} ${lengthUnit} thick` : `— thick`;

    return (
      <React.Fragment>
      <V3CollapsibleSection
        title="Module"
        icon={ModuleIcon}
        iconVariant="blue"
        defaultOpen={false}
        summarySlot={
          <React.Fragment>
            <span className="v3-sum-chip">{mfgLabel}</span>
            <span className="v3-sum-chip">{modelLabel}</span>
            <span className="v3-sum-chip">{wattsLabel}</span>
            <span className="v3-sum-chip">{dimsLabel}</span>
            <span className="v3-sum-chip">{thicknessChip}</span>
            <span className="v3-sum-more">+2 fields</span>
          </React.Fragment>
        }
      >
        {/* Unit toggle bar */}
        <div className="v3-module-unit-bar">
          <span className="v3-spec-lbl">Units</span>
          <V3UnitToggle
            options={['Imperial', 'Metric']}
            value={unitMode}
            onChange={onUnitChange}
          />
        </div>

        <V3SpecsTable flush={true}>
          <V3SpecRow label="Manufacturer">
            <div className="v3-mfr-combo v3-mfr-combo--inline" ref={this.mfgRef}>
              <input className="v3-spec-val" type="text"
                placeholder={this.props.mfgsLoading ? 'Loading…' : 'Type or select…'}
                value={this.state.mfgFilter}
                onChange={(e) => this.setState({ mfgFilter: e.target.value, mfgOpen: true })}
                onFocus={() => this.setState({ mfgOpen: true })}
                autoComplete="off" />
              {this.state.mfgOpen && this.getFilteredMfgs().length > 0 && (
                <ul className="v3-mfr-dropdown open">
                  {this.getFilteredMfgs().map((mfg) => (
                    <li key={mfg.id} onClick={() => this.selectMfg(mfg)}>{mfg.name}</li>
                  ))}
                </ul>
              )}
            </div>
          </V3SpecRow>
          <V3SpecRow label="Model">
            <div className="v3-mfr-combo v3-mfr-combo--inline" ref={this.modelRef}>
              <input className="v3-spec-val" type="text"
                placeholder={this.props.modelsLoading ? 'Loading…' : 'Type or select…'}
                value={this.state.modelFilter}
                onChange={(e) => this.setState({ modelFilter: e.target.value, modelOpen: true })}
                onFocus={() => this.setState({ modelOpen: true })}
                autoComplete="off" />
              {this.state.modelOpen && this.getFilteredModels().length > 0 && (
                <ul className="v3-mfr-dropdown open">
                  {this.getFilteredModels().map((model) => {
                    const sameDims = this.hasSameDims(model) || model.id === CUSTOM_MODEL_ID;
                    return (
                      <li key={model.id}
                        style={{ fontWeight: sameDims ? 700 : 400, color: sameDims ? '#0A3C5D' : '#9ab' }}
                        onClick={() => this.selectModel(model)}>
                        {model.name}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </V3SpecRow>
          <V3SpecRow label="Output">
            <input
              className="v3-spec-val"
              type="number"
              placeholder="—"
              value={outputValue}
              onChange={this.updateSpec('output', 1)}
            />
            <span className="v3-spec-unit">W</span>
          </V3SpecRow>
          <V3SpecRow label="Width">
            <input
              className={`v3-spec-val${errors.width ? ' v3-spec-val-error' : ''}`}
              type="number"
              placeholder="—"
              value={widthValue}
              onChange={this.onDimInput('widthIn')}
              onBlur={this.commitDim('widthIn', isMetric ? 1 / IN_TO_MM : 1)}
              onKeyDown={this.onDimKeyDown}
            />
            <span className="v3-spec-unit">{lengthUnit}</span>
          </V3SpecRow>
          {errors.width && (
            <V3SpecRow label="">
              <span className="v3-specs-row-err">{errors.width}</span>
            </V3SpecRow>
          )}
          <V3SpecRow label="Length">
            <input
              className={`v3-spec-val${errors.length ? ' v3-spec-val-error' : ''}`}
              type="number"
              placeholder="—"
              value={lengthValue}
              onChange={this.onDimInput('lengthIn')}
              onBlur={this.commitDim('lengthIn', isMetric ? 1 / IN_TO_MM : 1)}
              onKeyDown={this.onDimKeyDown}
            />
            <span className="v3-spec-unit">{lengthUnit}</span>
          </V3SpecRow>
          {errors.length && (
            <V3SpecRow label="">
              <span className="v3-specs-row-err">{errors.length}</span>
            </V3SpecRow>
          )}
          <V3SpecRow label="Thickness">
            <input
              className={`v3-spec-val${errors.thickness ? ' v3-spec-val-error' : ''}`}
              type="number"
              placeholder="—"
              value={thicknessValue}
              onChange={this.onDimInput('thicknessIn')}
              onBlur={this.commitDim('thicknessIn', isMetric ? 1 / IN_TO_MM : 1)}
              onKeyDown={this.onDimKeyDown}
            />
            <span className="v3-spec-unit">{lengthUnit}</span>
          </V3SpecRow>
          {errors.thickness && (
            <V3SpecRow label="">
              <span className="v3-specs-row-err">{errors.thickness}</span>
            </V3SpecRow>
          )}
          <V3SpecRow label="Weight">
            <input
              className="v3-spec-val"
              type="number"
              placeholder="—"
              value={weightValue}
              onChange={this.updateSpec('weightLbs', isMetric ? 1 / LB_TO_KG : 1)}
            />
            <span className="v3-spec-unit">{weightUnit}</span>
          </V3SpecRow>
          <V3SpecRow label="Area">
            <input
              className="v3-spec-val"
              type="number"
              placeholder="—"
              readOnly={true}
              value={areaDisp !== undefined ? fmt(areaDisp, areaDecimals) : ''}
            />
            <span className="v3-spec-unit">{areaUnit}</span>
          </V3SpecRow>
        </V3SpecsTable>
      </V3CollapsibleSection>
      {this.state.pendingMfgChange && (
        <div className="v3-modal-overlay" onClick={this.cancelMfgChange}>
          <div className="v3-modal-card" style={{ width: 420 }} onClick={(e) => e.stopPropagation()}>
            <div className="v3-modal-top">
              <div className="v3-modal-step-label">WARNING</div>
              <div className="v3-modal-title">Change Manufacturer?</div>
            </div>
            <div className="v3-modal-body">
              <div className="v3-modal-subtitle">
                Changing manufacturer will clear all panels across all roof areas. This cannot be undone.
              </div>
            </div>
            <div className="v3-modal-footer">
              <V3Button variant="ghost" size="md" onClick={this.cancelMfgChange}>Cancel</V3Button>
              <V3Button variant="primary" size="md" onClick={this.confirmMfgChange}>Continue</V3Button>
            </div>
          </div>
        </div>
      )}
      {this.state.pendingModelChange && (
        <div className="v3-modal-overlay" onClick={this.cancelModelChange}>
          <div className="v3-modal-card" style={{ width: 420 }} onClick={(e) => e.stopPropagation()}>
            <div className="v3-modal-top">
              <div className="v3-modal-step-label">WARNING</div>
              <div className="v3-modal-title">Change Module?</div>
            </div>
            <div className="v3-modal-body">
              <div className="v3-modal-subtitle">
                The selected module has different dimensions. All panels across all roof areas will be cleared. This cannot be undone.
              </div>
            </div>
            <div className="v3-modal-footer">
              <V3Button variant="ghost" size="md" onClick={this.cancelModelChange}>Cancel</V3Button>
              <V3Button variant="primary" size="md" onClick={this.confirmModelChange}>Continue</V3Button>
            </div>
          </div>
        </div>
      )}
      {this.state.pendingDimChange && (
        <div className="v3-modal-overlay" onClick={this.cancelDimChange}>
          <div className="v3-modal-card" style={{ width: 420 }} onClick={(e) => e.stopPropagation()}>
            <div className="v3-modal-top">
              <div className="v3-modal-step-label">WARNING</div>
              <div className="v3-modal-title">Change Module Dimensions?</div>
            </div>
            <div className="v3-modal-body">
              <div className="v3-modal-subtitle">
                Changing panel dimensions will clear all panels across all roof areas. This cannot be undone.
              </div>
            </div>
            <div className="v3-modal-footer">
              <V3Button variant="ghost" size="md" onClick={this.cancelDimChange}>
                Cancel
              </V3Button>
              <V3Button variant="primary" size="md" onClick={this.confirmDimChange}>
                Continue
              </V3Button>
            </div>
          </div>
        </div>
      )}
      </React.Fragment>
    );
  }
}

function mapStateToProps(appState: appState) {
  const pc = appState.projectConfiguration || ({} as any);
  const env = pc.projectEnvConfig || {};
  const ms = (appState as any).moduleSelector || {};
  // Three sources in priority order:
  //  1. projectEnvConfig.manufacturer_name / model_name / module_specs —
  //     the UX-layer names set by the 4-step modal on a fresh create.
  //  2. moduleSelector — canonical slice populated by loadProject() →
  //     setPanelsModel() after GET /api/v1/project/:id/. The existing
  //     projectEnvConfig transformers in utils/projectConfigurationData/
  //     strip module_* fields, so env won't have them after a GET
  //     round-trip. mfgs[selectedMfgId].name + modelData give us the
  //     display names and specs instead.
  const toNum = (v: any): number | undefined => {
    if (v === undefined || v === null || v === '') return undefined;
    const n = typeof v === 'number' ? v : parseFloat(v);
    return isNaN(n) ? undefined : n;
  };
  const modelData = ms.modelData || {};
  const mfgFromList = Array.isArray(ms.mfgs)
    ? ms.mfgs.find((m: any) => m && m.id === ms.selectedMfgId)
    : undefined;
  // After loadProject's FETCH_MFGS_AND_MODELS chain resolves, SET_MODEL is
  // re-dispatched with customModel — which has specs but no `.name`. Look
  // the name up in the fetched models list instead.
  const modelFromList = Array.isArray(ms.models)
    ? ms.models.find((m: any) => m && m.id === ms.selectedModelId)
    : undefined;
  const nestedSpecs = env.module_specs as ModuleSpecs | undefined;
  const widthIn = nestedSpecs?.widthIn ?? toNum(modelData.width) ?? toNum(env.module_width);
  const lengthIn = nestedSpecs?.lengthIn ?? toNum(modelData.height) ?? toNum(env.module_length);
  const derivedSpecs: ModuleSpecs = {
    output: nestedSpecs?.output ?? toNum(modelData.watts) ?? toNum(env.module_watts),
    widthIn,
    lengthIn,
    thicknessIn: nestedSpecs?.thicknessIn ?? toNum(modelData.thickness) ?? toNum(env.module_thickness),
    weightLbs: nestedSpecs?.weightLbs ?? toNum(modelData.weight) ?? toNum(env.module_weight),
    areaFt: nestedSpecs?.areaFt ?? (widthIn && lengthIn ? (widthIn * lengthIn) / 144 : undefined),
  };
  return {
    manufacturerName: env.manufacturer_name || (mfgFromList && mfgFromList.name) || env.module_manufacturer || '',
    modelName: env.model_name || (modelFromList && modelFromList.name) || modelData.name || env.module_model || '',
    specs: derivedSpecs,
    panelCount: appState.panels && appState.panels.panels ? appState.panels.panels.length : 0,
    productId: pc.productId,
    inputUnit: pc.inputUnit,
    productSlug: (() => {
      const numId = pc.productId;
      if (!numId) return '';
      const entry = Object.entries(products).find(([, v]) => v === numId);
      return entry ? entry[0] : '';
    })(),
    mfgs: ms.mfgs || [],
    models: ms.models || [],
    mfgsLoading: !!ms.mfgsLoading || !!ms.allInLoading,
    modelsLoading: !!ms.modelsLoading,
  };
}

export default connect(mapStateToProps)(ModuleSection);
